
import { RangeResponseFilter } from '@shared/filters/entities/fetch-filters.response';
import { AirlinesWidget, PriceWidget, HourWidget } from '@app/entities/flight-results-widget';
import {
  PRICE_WIDGET_LABELS,
  PRICE_WIDGET_ICONS,
  DEPARTURE_WIDGET_LABELS,
  DEPARTURE_WIDGET_ICONS,
  SECTOR_DURATIONS_LABELS,
  SECTOR_DURATIONS_ICONS
} from '@flight-results/constants/filters.constants';
import { PriceWidgetTypes, DepartureWidgetTypes, FlightDurationWidgetTypes } from '@flight-results/entities/widget';
import { stringTimeToSeconds } from '@flight-results/mappers/filters.mapper';

export function searchAirlinesByLowerPrice(airlines: AirlinesWidget[]): AirlinesWidget[] {
  const airlinesFilteredByPrice: AirlinesWidget[] = [];
  const airlinesArray = [...new Set(airlines.map(a => a.Text))];
  airlinesArray.forEach(Text => {
    const airlineMin = airlines
      .filter(a => a.Text === Text)
      .reduce((res, obj) => (obj.MinPrice < res.MinPrice) ? obj : res);
    airlinesFilteredByPrice.push(airlineMin);
  });
  return [...airlinesFilteredByPrice]
    .sort((a, b) => a.MinPrice - b.MinPrice)
    .map((airline) => ({...airline, AirlineName: airline.AirlineName || airline.Text, Disabled: false}))
    .sort((moduleA, moduleB) => (moduleA.Disabled === moduleB.Disabled) ? 0 : moduleA.Disabled ? 1 : -1);
}

function calculateLimitPrice(
  type: string,
  maxPrice: number,
  minPrice: number,
  limitPrice: number): number[] {
  switch (type) {
    case PriceWidgetTypes.Cheap:
      return [minPrice, (minPrice + limitPrice)];
    case PriceWidgetTypes.Middle:
      return [(minPrice + limitPrice), (maxPrice - limitPrice)];
    case PriceWidgetTypes.High:
      return [(maxPrice - limitPrice), maxPrice];
  }
}

export function mapPriceForWidget(minPrice: number, maxPrice: number): PriceWidget[] {
  const equalParts = 3;
  const limitPrice = Math.round((maxPrice - minPrice) / equalParts);
  const prices = [PriceWidgetTypes.Cheap, PriceWidgetTypes.Middle, PriceWidgetTypes.High];
  return prices.map((price: string) => {
    return {
      Type: price,
      Label: PRICE_WIDGET_LABELS[price],
      Icon: PRICE_WIDGET_ICONS[price],
      PriceLimit: calculateLimitPrice(price, maxPrice, minPrice, limitPrice)
    };
  });
}

function setHourLimit(type: string, departureTime: RangeResponseFilter, index: number, length: number): string[] {
  let hours: string[];
  switch (type) {
    case DepartureWidgetTypes.Morning:
      hours = ['0', '12'];
      break;
    case DepartureWidgetTypes.Afternoon:
      hours = ['12', '19'];
      break;
    case DepartureWidgetTypes.Evening:
      hours = ['19', '24'];
      break;
  }
  if (index === 0) {
    hours[0] = departureTime.From.toString();
  }

  if (index === length - 1) {
    hours[1] = departureTime.To.toString();
  }
  return hours;
}

export function mapDepartureForWidget(departureTimes: RangeResponseFilter[], sector: string): HourWidget[] {
  const maxMorningTime = 11;
  const minEveningTime = 19;
  const departureTimeInfo = departureTimes.find(flight => flight.Text === sector);
  let times = [DepartureWidgetTypes.Morning, DepartureWidgetTypes.Afternoon, DepartureWidgetTypes.Evening];
  if (departureTimeInfo) {
    if (departureTimeInfo.From as number > maxMorningTime) {
      times = times.filter(val => val !== 'Morning');
    }
    if (departureTimeInfo.To as number <= minEveningTime) {
      times = times.filter(val => val !== 'Evening');
    }
    return times.map((time: string, index) => {
      return {
        'Type': time,
        'Label': DEPARTURE_WIDGET_LABELS[time],
        'Icon': DEPARTURE_WIDGET_ICONS[time],
        'HourLimits': setHourLimit(time, departureTimeInfo, index, times.length)
      };
    });
  } else return [];

}

export function mapFlightDurationForWidget(flightDurationTimes: RangeResponseFilter[], sector: number): HourWidget[] {
  const departureTimeInfo = flightDurationTimes?.[sector];
  const fromTime = stringTimeToSeconds(departureTimeInfo?.From?.toString());
  const toTime = stringTimeToSeconds(departureTimeInfo?.To?.toString());

  const splitMeasure = (toTime - fromTime) / 3;
  const splitOne = fromTime + splitMeasure;
  const splitTwo = fromTime + (splitMeasure * 2);

  const times = [FlightDurationWidgetTypes.Low, FlightDurationWidgetTypes.Intermediate, FlightDurationWidgetTypes.High];

  return times.map((time: string, index) => ({
    'Type': time,
    'Label': SECTOR_DURATIONS_LABELS[time],
    'Icon': SECTOR_DURATIONS_ICONS[time],
    'HourLimits': index === 0 ?
      [fromTime.toString(), splitOne.toString()] :
      index === 1 ? [splitOne.toString(), splitTwo.toString()] :
        [splitTwo.toString(), toTime.toString()]
  }));
}
